import { put, takeLatest, select, delay } from 'redux-saga/effects';
import analytics from '@xto10x/core/lib/analytics';
import {
  HeaderLeftTabs,
  ErrorMsg,
  AlertActionTypes,
  ToastActionType
} from 'utils/constants';
import { getObjectiveFromPath } from 'utils/objectives.utils';
import { AnalyticsCategory } from 'utils/analytics.constants';
import {
  getObjectivesSelector,
  getPathMapSelector,
  getFlatObjectivesSelector
} from 'containers/Objective/store/reducer';
import { getCurrentUserInfoSelector } from 'containers/App/store/reducer';
import objectivesApi from 'services/objectivesApi';

import {
  showAppLoading,
  hideAppLoading,
  showAlertMessage,
  saveHighlightedObjective
} from 'containers/App/store/actions';
import {
  saveNewKR,
  deleteObjectiveInList,
  storeRecentlyUpdatedObjectiveDetails,
  linkObjectiveRequest,
  saveRecentlyDroppedObjective
} from 'containers/Objective/store/actions';
import { getSelectedTeamSelector } from 'containers/Header/store/reducer';
import { getValueFromQueryParam } from '@xto10x/core/lib/app.utils';
import {
  actions,
  reorderObjectives,
  reorderObjectivesUnderSameParent,
  reorderObjectivesUnderDiffParent,
  revertObjectiveMoveActionRequest,
  showOKRHierarchyToast
} from './actions';
import { getOrderingVersionSelector } from '../../Objective/store/reducer';
import { showOKRHierarchyAlertMessage } from '../../App/store/actions';
import {
  appendCustomRangeQueryParamsToRequest,
  epochToStandardDateString,
  isHeaderLeftTabs
} from '../../../utils/app.utils';
import { QueryParam } from '../../../utils/constants';
import {
  getObjectiveCyclesSelector,
  getSearchObjectiveParams
} from '../../Header/store/reducer';
import { fetchFilteredObjectives } from '../../Objective/store/actions';
import { getMatchingCycleFromCustomRange } from '../../../utils/objectives.utils';

function* reorderObjectivesWorker({
  objective_id,
  newIndex,
  oldIndex,
  isUndoAction
}) {
  try {
    yield put(showAppLoading());
    const selectedView = yield select(getSelectedTeamSelector);
    const objectivesList = yield select(getObjectivesSelector);
    const flatObjectives = yield select(getFlatObjectivesSelector);
    const currentUserInfo = yield select(getCurrentUserInfoSelector);
    const ordering_version = yield select(getOrderingVersionSelector);
    const objCycles = yield select(getObjectiveCyclesSelector);
    const reference_objective =
      objectivesList[
        flatObjectives[newIndex]?.positionIndex?.toString()?.split('.')[0]
      ];
    const currentCycle = getMatchingCycleFromCustomRange(objCycles);

    const requestBody = {};
    let view = '';
    if (selectedView?.tag_id === HeaderLeftTabs[0].tag_id) {
      requestBody.view_type = 'MY_OBJECTIVE';
      view = 'MY_OBJECTIVE';
      requestBody.view_identifier = currentUserInfo.user_reference_id;
    } else if (selectedView?.tag_id === HeaderLeftTabs[1].tag_id) {
      view = 'DIRECT_REPORTS';
      requestBody.view_type = 'DIRECT_REPORTS';
      requestBody.view_identifier = currentUserInfo.user_reference_id;
    } else if (selectedView?.tag_id === HeaderLeftTabs[2].tag_id) {
      requestBody.view_type = 'ALL_OBJECTIVE';
      view = 'ALL_OBJECTIVE';
    } else {
      requestBody.view_type = 'TEAM';
      view = 'TEAM';
      requestBody.view_identifier = selectedView.tag_id;
    }
    requestBody.reference_objective_id = reference_objective?.objective_id;
    requestBody.objective_position = newIndex < oldIndex ? 'BEFORE' : 'AFTER';
    requestBody.okr_cycle_id = currentCycle?.id;
    requestBody.ordering_version = ordering_version;

    yield objectivesApi.reorderObjectives(objective_id, requestBody);
    analytics.trackEvent(
      AnalyticsCategory.drag_and_drop,
      'reorder_objectives',
      view
    );
    yield put(reorderObjectives(objective_id, newIndex, oldIndex));
    yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
    const matchingObj = flatObjectives.find(
      obj => obj.objective_id === objective_id
    );
    yield put(saveRecentlyDroppedObjective(matchingObj));
    const alertAction = {
      actionText: 'Know more',
      actionType: ToastActionType.LINK,
      link:
        'https://docs.xto10x.com/getting-started-on-10xgoals/drag-and-drop-okrs'
    };
    const undoAlertAction = {
      showSubSection: true,
      subSection: {
        actionType: ToastActionType.UNDO_DND_ACTION,
        alertText: 'Click on Undo to revert the changes',
        imageUrl: '/assets/images/refresh-red.svg',
        buttonText: 'Undo',
        actionData: {
          type: 'ROOT_REORDER',
          objective_id,
          oldIndex: newIndex,
          newIndex: oldIndex
        }
      }
    };
    if (!isUndoAction)
      yield put(
        showAlertMessage(
          AlertActionTypes.NO_ICON,
          view === 'ALL_OBJECTIVE' || view === 'TEAM'
            ? '👁 This order change is visible to everyone'
            : 'This order change is only visible to you',
          view === 'ALL_OBJECTIVE' || view === 'TEAM'
            ? { ...alertAction, ...undoAlertAction }
            : undoAlertAction
        )
      );

    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update view order', e);
  }
}

function* addToObjectiveTreeRequestWorker({
  dragged_objective_id,
  new_parent_objective_id,
  parentObjectiveId,
  draggingLinkedObjective,
  sourceIndex
}) {
  try {
    const objectivesList = yield select(getObjectivesSelector);
    const flatObjectives = yield select(getFlatObjectivesSelector);
    const selectedView = yield select(getSelectedTeamSelector);
    let view_type = '';

    if (selectedView.tag_id === HeaderLeftTabs[0].tag_id) {
      view_type = 'MY_OBJECTIVE';
    } else if (selectedView.tag_id === HeaderLeftTabs[1].tag_id) {
      view_type = 'DIRECT_REPORTS';
    } else if (selectedView.tag_id === HeaderLeftTabs[2].tag_id) {
      view_type = 'ALL_OBJECTIVE';
    } else {
      view_type = 'TEAM';
    }

    if (draggingLinkedObjective) {
      // if linked objective added to tree, call link api
      const objective = flatObjectives.find(
        obj => obj.objective_id === new_parent_objective_id
      );
      const objectiveToBeLinked = flatObjectives[sourceIndex];

      if (dragged_objective_id === new_parent_objective_id) {
        yield put(
          showAlertMessage('error', ErrorMsg.OBJECTIVE_TO_LINK_IS_PARENT_ERROR)
        );
        yield put(hideAppLoading());
        return;
      }

      analytics.trackEvent(
        AnalyticsCategory.drag_and_drop,
        'move_paste_linked_objective_using_drag',
        view_type
      );
      yield put(linkObjectiveRequest(objective, objectiveToBeLinked));
      yield put(storeRecentlyUpdatedObjectiveDetails(dragged_objective_id));
      yield put(saveRecentlyDroppedObjective(objectiveToBeLinked));
      yield put(hideAppLoading());
      return;
    }

    const objectivePath = yield select(getPathMapSelector);
    const parentObjectiveIndex = flatObjectives.findIndex(
      obj => obj.objective_id === new_parent_objective_id
    );
    const newParent = flatObjectives[parentObjectiveIndex];
    const currentObjective = getObjectiveFromPath(
      objectivesList,
      objectivePath[dragged_objective_id],
      dragged_objective_id
    );

    const requestBody = {
      current_parent_objective_id: parentObjectiveId,
      new_parent_objective_id,
      ordering_version: newParent?.kr_order_version
    };

    const { data } = yield objectivesApi.alignObjective(
      dragged_objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(dragged_objective_id));
    analytics.trackEvent(
      AnalyticsCategory.drag_and_drop,
      'move_paste_using_drag',
      view_type
    );

    yield put(
      deleteObjectiveInList({
        ...data
      })
    );
    yield delay(100);
    yield put(
      saveNewKR(
        { ...currentObjective, root_objective: false },
        new_parent_objective_id
      )
    );
    if (!newParent.isOpen) yield put(saveHighlightedObjective(newParent, true));
    yield put(saveRecentlyDroppedObjective(data));
    try {
      /* data required for reverting the current action */
      const objective = flatObjectives.find(
        obj => obj.objective_id === dragged_objective_id
      );
      const currentParentObjective = flatObjectives.find(
        obj => obj.objective_id === parentObjectiveId
      );
      let isLastObjective = false;
      let isLastKr = false;
      let previousRootObjective = null;
      let nextRootObjective = null;
      let nextObjective = null;
      let movedKrIndexInParentObj = -1;
      let useCaseIdentifier = -1;
      if (!parentObjectiveId) {
        isLastObjective = flatObjectives.length - 1 === sourceIndex;
        if (isLastObjective) {
          previousRootObjective = flatObjectives.find(
            obj => obj.positionIndex === objective.positionIndex - 1
          );
          useCaseIdentifier = 0;
        } else {
          nextRootObjective = flatObjectives.find(
            obj => obj.positionIndex === objective.positionIndex + 1
          );
          useCaseIdentifier = 1;
        }
      } else if (parentObjectiveId && currentParentObjective) {
        const parentKeyResults = currentParentObjective?.key_results;
        movedKrIndexInParentObj = parentKeyResults?.findIndex(
          kr => kr.objective_id === dragged_objective_id
        );
        if (parentKeyResults.length === 1) {
          useCaseIdentifier = 2;
        } else {
          if (movedKrIndexInParentObj === parentKeyResults.length - 1)
            isLastKr = true;
          if (isLastKr) {
            useCaseIdentifier = 3;
          } else {
            nextObjective = parentKeyResults[movedKrIndexInParentObj + 1];
            useCaseIdentifier = 4;
          }
        }
      } else if (parentObjectiveId && !currentParentObjective) {
        isLastObjective = flatObjectives.length - 1 === sourceIndex;
        if (isLastObjective) {
          previousRootObjective = flatObjectives.find(
            obj => obj.positionIndex === objective.positionIndex - 1
          );
          useCaseIdentifier = 5;
        } else {
          nextRootObjective = flatObjectives.find(
            obj => obj.positionIndex === objective.positionIndex + 1
          );
          useCaseIdentifier = 6;
        }
      }
      const undoAlertAction = {
        showSubSection: true,
        subSection: {
          actionType: ToastActionType.UNDO_DND_ACTION,
          alertText: 'Click on Undo to revert the changes',
          imageUrl: '/assets/images/refresh-red.svg',
          buttonText: 'Undo',
          actionData: {
            type: 'ADD_TO_OBJECTIVE_TREE',
            objective_id: dragged_objective_id,
            parent_objective_id: parentObjectiveId,
            new_parent_objective_id,
            currentObjective: objective,
            direction: parentObjectiveIndex > sourceIndex ? 'down' : 'up',
            parentPositionIndex: currentParentObjective?.positionIndex,
            newParentPositionIndex: newParent.positionIndex,
            parentKrOrderVersion: currentParentObjective?.kr_order_version,
            previousRootObjective,
            nextRootObjective,
            nextObjective,
            movedKrIndexInParentObj,
            useCaseIdentifier,
            isCompanyObjective: objective.company_objective
          }
        }
      };
      yield put(
        showAlertMessage(AlertActionTypes.NO_ICON, '', undoAlertAction)
      );
    } catch (e) {
      analytics.trackError('add_to_objective_tree_undo_action', e);
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('drop on objective', e);
  }
}

function* reorderUnderSameParentRequestWorker({
  objective_id,
  parent_objective_id,
  destinationIndex,
  sourceIndex,
  positionIndex,
  isUndoAction
}) {
  try {
    yield put(showAppLoading());
    const flatObjectivesList = yield select(getFlatObjectivesSelector);
    const reference_objective = flatObjectivesList[destinationIndex];
    const parent = flatObjectivesList.find(
      obj => obj.objective_id === parent_objective_id
    );
    const selectedView = yield select(getSelectedTeamSelector);
    const objCycles = yield select(getObjectiveCyclesSelector);
    const currentCycle = getMatchingCycleFromCustomRange(objCycles);

    const requestBody = {};
    requestBody.view_type = 'KEY_RESULT';
    requestBody.view_identifier = parent_objective_id;
    requestBody.reference_objective_id = reference_objective.objective_id;
    requestBody.objective_position =
      destinationIndex < sourceIndex ? 'BEFORE' : 'AFTER';
    requestBody.okr_cycle_id = currentCycle?.id;
    requestBody.ordering_version = parent.kr_order_version;

    yield objectivesApi.reorderObjectives(objective_id, requestBody);
    yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
    yield put(
      reorderObjectivesUnderSameParent(
        objective_id,
        parent_objective_id,
        destinationIndex,
        sourceIndex,
        positionIndex
      )
    );
    const matchingObj = flatObjectivesList.find(
      obj => obj.objective_id === objective_id
    );
    yield put(saveRecentlyDroppedObjective(matchingObj));

    let view_type = '';

    if (selectedView.tag_id === HeaderLeftTabs[0].tag_id) {
      view_type = 'MY_OBJECTIVE';
    } else if (selectedView.tag_id === HeaderLeftTabs[1].tag_id) {
      view_type = 'DIRECT_REPORTS';
    } else if (selectedView.tag_id === HeaderLeftTabs[2].tag_id) {
      view_type = 'ALL_OBJECTIVE';
    } else {
      view_type = 'TEAM';
    }

    analytics.trackEvent(
      AnalyticsCategory.drag_and_drop,
      'reorder_krs',
      view_type
    );
    yield put(hideAppLoading());
    const alertAction = {
      actionText: 'Know more',
      actionType: ToastActionType.LINK,
      link:
        'https://docs.xto10x.com/getting-started-on-10xgoals/drag-and-drop-okrs'
    };
    const undoAlertAction = {
      showSubSection: true,
      subSection: {
        actionType: ToastActionType.UNDO_DND_ACTION,
        alertText: 'Click on Undo to revert the changes',
        imageUrl: '/assets/images/refresh-red.svg',
        buttonText: 'Undo',
        actionData: {
          type: 'REORDER_WITHIN_SAME_PARENT',
          objective_id,
          parent_objective_id,
          sourceIndex: destinationIndex,
          destinationIndex: sourceIndex
        }
      }
    };
    if (!isUndoAction)
      yield put(
        showAlertMessage(
          AlertActionTypes.NO_ICON,
          '👁 This order change is visible to everyone',
          { ...alertAction, ...undoAlertAction }
        )
      );
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('reorder objective under same parent', e);
  }
}

function* reorderUnderDiffParentRequestWorker({
  objective_id,
  parent_objective_id,
  destinationIndex,
  sourceIndex,
  positionIndex,
  draggingLinkedObjective
}) {
  try {
    yield put(showAppLoading());
    const flatObjectivesList = yield select(getFlatObjectivesSelector);
    const selectedView = yield select(getSelectedTeamSelector);
    const cycles = yield select(getObjectiveCyclesSelector);
    const reference_objective =
      destinationIndex > sourceIndex &&
      flatObjectivesList[destinationIndex + 1]?.positionIndex.toString()
        .length >
        flatObjectivesList[destinationIndex].positionIndex.toString().length
        ? flatObjectivesList[destinationIndex + 1]
        : flatObjectivesList[destinationIndex];

    const parent = flatObjectivesList.find(
      obj => obj.objective_id === reference_objective.parent_objective_id
    );

    // if linkedObjective is dragged to root
    if (draggingLinkedObjective && !reference_objective.parent_objective_id) {
      yield put(
        showAlertMessage('error', 'Objective cannot be moved to this position.')
      );
      yield put(hideAppLoading());
      return;
    }

    const csd = getValueFromQueryParam(QueryParam.CUSTOM_DATE_RANGE_START_DATE);
    const ced = getValueFromQueryParam(QueryParam.CUSTOM_DATE_RANGE_END_DATE);

    const requestBody = {};
    requestBody.current_parent_objective_id = parent_objective_id;
    requestBody.new_parent_objective_id =
      destinationIndex === 0 ? null : reference_objective.parent_objective_id;
    requestBody.start_date = epochToStandardDateString(csd)?.[0];
    requestBody.due_date = epochToStandardDateString(ced)?.[0];

    let view_type = '';

    if (selectedView?.tag_id === HeaderLeftTabs[0].tag_id) {
      view_type = 'MY_OBJECTIVE';
    } else if (selectedView?.tag_id === HeaderLeftTabs[1].tag_id) {
      view_type = 'DIRECT_REPORTS';
    } else if (selectedView?.tag_id === HeaderLeftTabs[2].tag_id) {
      view_type = 'ALL_OBJECTIVE';
    } else {
      view_type = 'TEAM';
    }

    if (
      reference_objective.parent_objective_id &&
      reference_objective.objective_id !== objective_id
    ) {
      requestBody.objective_prioritization_request = {
        view_type: 'KEY_RESULT',
        view_identifier: reference_objective.parent_objective_id,
        reference_objective_id: reference_objective.objective_id,
        objective_position:
          destinationIndex < sourceIndex
            ? 'BEFORE'
            : flatObjectivesList[destinationIndex + 1]?.positionIndex.toString()
                .length >= reference_objective.positionIndex.toString().length
            ? 'BEFORE'
            : 'AFTER',
        ordering_version: parent?.kr_order_version,
        okr_cycle_id: getMatchingCycleFromCustomRange(cycles)?.id
      };
    }

    if (draggingLinkedObjective) {
      const objective = flatObjectivesList.find(
        obj => obj.objective_id === reference_objective.parent_objective_id
      );
      const objectiveToBeLinked = flatObjectivesList[sourceIndex];
      if (objective_id === parent_objective_id) {
        yield put(
          showAlertMessage('error', ErrorMsg.OBJECTIVE_TO_LINK_IS_PARENT_ERROR)
        );
        yield put(hideAppLoading());
        return;
      }
      yield put(
        linkObjectiveRequest(
          objective,
          objectiveToBeLinked,
          requestBody.objective_prioritization_request,
          {
            objective_id,
            parent_objective_id,
            destinationIndex,
            sourceIndex,
            positionIndex
          }
        )
      );
      yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
      yield put(saveRecentlyDroppedObjective(objectiveToBeLinked));
      analytics.trackEvent(
        AnalyticsCategory.drag_and_drop,
        'move_paste_linked_objective_using_drag',
        view_type
      );
      yield put(hideAppLoading());
      return;
    }

    const { data } = yield objectivesApi.alignObjective(
      objective_id,
      requestBody
    );
    yield put(storeRecentlyUpdatedObjectiveDetails(objective_id));
    yield put(saveRecentlyDroppedObjective(data));
    yield put(
      reorderObjectivesUnderDiffParent(
        {
          ...data,
          key_results_count: flatObjectivesList[sourceIndex].key_results_count
        },
        objective_id,
        parent_objective_id,
        destinationIndex,
        sourceIndex,
        positionIndex
      )
    );
    try {
      /* data required for reverting the current action */
      const currentObjective = flatObjectivesList.find(
        obj => obj.objective_id === objective_id
      );
      const currentParentObjective = flatObjectivesList.find(
        obj => obj.objective_id === parent_objective_id
      );
      const newParentObjective = flatObjectivesList.find(
        obj => obj.objective_id === reference_objective?.parent_objective_id
      );
      let isLastObjective = false;
      let isOnlyKr = false;
      let isLastKr = false;
      let previousRootObjective = null;
      let nextRootObjective = null;
      let nextObjective = null;
      let movedKrIndexInParentObj = -1;
      let useCaseIdentifier = -1;

      if (!parent_objective_id && !currentParentObjective) {
        isLastObjective = sourceIndex === flatObjectivesList.length - 1;
        if (isLastObjective) {
          previousRootObjective = flatObjectivesList.find(
            obj => obj.positionIndex === positionIndex - 1
          );
          useCaseIdentifier = 0;
        } else {
          nextRootObjective = flatObjectivesList.find(
            obj => obj.positionIndex === positionIndex + 1
          );
          useCaseIdentifier = 1;
        }
      } else if (parent_objective_id && currentParentObjective) {
        const position = positionIndex.split('.');
        const objIndexInTree = +position[position.length - 1];
        isLastKr =
          objIndexInTree === currentParentObjective.key_results_count - 1;
        isOnlyKr = objIndexInTree === 0 && isLastKr;
        if (isOnlyKr) {
          useCaseIdentifier = 2;
        } else if (isLastKr) useCaseIdentifier = 3;
        else {
          nextObjective =
            currentParentObjective.key_results[objIndexInTree + 1];
          movedKrIndexInParentObj = objIndexInTree;
          useCaseIdentifier = 4;
        }
      } else {
        isLastObjective = sourceIndex === flatObjectivesList.length - 1;
        if (isLastObjective) {
          previousRootObjective = flatObjectivesList.find(
            obj => obj.positionIndex === positionIndex - 1
          );
          useCaseIdentifier = 5;
        } else {
          nextRootObjective = flatObjectivesList.find(
            obj => obj.positionIndex === positionIndex + 1
          );
          useCaseIdentifier = 6;
        }
      }
      const undoAlertAction = {
        showSubSection: true,
        subSection: {
          actionType: ToastActionType.UNDO_DND_ACTION,
          alertText: 'Click on Undo to revert the changes',
          imageUrl: '/assets/images/refresh-red.svg',
          buttonText: 'Undo',
          actionData: {
            type: 'REORDER_UNDER_DIFFERENT_PARENT',
            objective_id,
            parent_objective_id,
            new_parent_objective_id: requestBody.new_parent_objective_id,
            currentObjective,
            direction: destinationIndex > sourceIndex ? 'down' : 'up',
            parentPositionIndex: currentParentObjective?.positionIndex,
            newParentPositionIndex: newParentObjective?.positionIndex,
            parentKrOrderVersion: currentParentObjective?.kr_order_version,
            previousRootObjective,
            nextRootObjective,
            nextObjective,
            movedKrIndexInParentObj,
            useCaseIdentifier,
            isCompanyObjective: currentObjective.company_objective
          }
        }
      };
      yield put(
        showAlertMessage(AlertActionTypes.NO_ICON, '', undoAlertAction)
      );

      yield put(showOKRHierarchyToast());
    } catch (e) {
      analytics.trackError('reorder_under_diff_parent_request_undo_action', e);
    }
    analytics.trackEvent(
      AnalyticsCategory.drag_and_drop,
      'move_paste_using_drag',
      view_type
    );
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('move/paste objective using drag and drop', e);
  }
}

function* revertObjectiveMoveActionWorker({ undoActionData }) {
  try {
    const {
      objective_id,
      parent_objective_id,
      new_parent_objective_id,
      parentKrOrderVersion,
      previousRootObjective,
      nextRootObjective,
      nextObjective,
      useCaseIdentifier,
      isCompanyObjective
    } = undoActionData;
    yield put(showAppLoading());

    const selectedView = yield select(getSelectedTeamSelector);
    const currentUserInfo = yield select(getCurrentUserInfoSelector);
    const ordering_version = yield select(getOrderingVersionSelector);
    const cycles = yield select(getObjectiveCyclesSelector);

    const csd = getValueFromQueryParam(QueryParam.CUSTOM_DATE_RANGE_START_DATE);
    const ced = getValueFromQueryParam(QueryParam.CUSTOM_DATE_RANGE_END_DATE);

    const requestBody = {
      current_parent_objective_id: null,
      new_parent_objective_id: null,
      objective_prioritization_request: null,
      start_date: epochToStandardDateString(csd)?.[0],
      due_date: epochToStandardDateString(ced)?.[0]
    };
    requestBody.current_parent_objective_id = new_parent_objective_id;
    if ([0, 1, 5, 6].indexOf(useCaseIdentifier) !== -1) {
      if (selectedView?.tag_id === HeaderLeftTabs[0].tag_id) {
        requestBody.view_type = 'MY_OBJECTIVE';
        requestBody.view_identifier = currentUserInfo.user_reference_id;
      } else if (selectedView?.tag_id === HeaderLeftTabs[1].tag_id) {
        requestBody.view_type = 'DIRECT_REPORTS';
        requestBody.view_identifier = currentUserInfo.user_reference_id;
      } else if (selectedView?.tag_id === HeaderLeftTabs[2].tag_id) {
        requestBody.view_type = 'ALL_OBJECTIVE';
      } else {
        requestBody.view_type = 'TEAM';
        requestBody.view_identifier = selectedView.tag_id;
      }
      requestBody.ordering_version = ordering_version;
    }
    if ([2, 3, 4, 5, 6].indexOf(useCaseIdentifier) !== -1) {
      requestBody.new_parent_objective_id = parent_objective_id;
    }
    let data = null;
    switch (useCaseIdentifier) {
      case 0 /* Last root objective */:
        requestBody.reference_objective_id = previousRootObjective.objective_id;
        requestBody.objective_position = 'AFTER';
        data = (yield objectivesApi.alignObjective(objective_id, requestBody))
          .data;
        yield put(saveRecentlyDroppedObjective(data));
        yield put(revertObjectiveMoveActionRequest(undoActionData));
        if (isCompanyObjective)
          yield objectivesApi.updateObjectiveType(objective_id, {
            company_objective: true,
            version: data?.version,
            parent_objective_owner: parent_objective_id
          });
        break;
      case 1 /* Non-last root objective */:
        requestBody.reference_objective_id = nextRootObjective.objective_id;
        requestBody.objective_position = 'BEFORE';
        data = (yield objectivesApi.alignObjective(objective_id, requestBody))
          .data;
        yield put(saveRecentlyDroppedObjective(data));
        yield put(revertObjectiveMoveActionRequest(undoActionData));
        if (isCompanyObjective)
          yield objectivesApi.updateObjectiveType(objective_id, {
            company_objective: true,
            version: data?.version,
            parent_objective_owner: parent_objective_id
          });
        break;
      case 2 /* Only KR */:
      case 3:
        data = (yield objectivesApi.alignObjective(objective_id, requestBody))
          .data;
        yield put(saveRecentlyDroppedObjective(data));
        yield put(revertObjectiveMoveActionRequest(undoActionData));
        break;
      case 4 /* KR in between */:
        requestBody.objective_prioritization_request = {
          view_type: 'KEY_RESULT',
          view_identifier: parent_objective_id,
          reference_objective_id: nextObjective.objective_id,
          objective_position: 'BEFORE',
          ordering_version: parentKrOrderVersion,
          okr_cycle_id: getMatchingCycleFromCustomRange(cycles)?.id
        };
        data = (yield objectivesApi.alignObjective(objective_id, requestBody))
          .data;
        yield put(saveRecentlyDroppedObjective(data));
        yield put(revertObjectiveMoveActionRequest(undoActionData));
        break;
      case 5 /* Last KR (root) of an aligned objective */:
        requestBody.reference_objective_id = previousRootObjective.objective_id;
        requestBody.objective_position = 'AFTER';
        data = (yield objectivesApi.alignObjective(objective_id, requestBody))
          .data;
        yield put(saveRecentlyDroppedObjective(data));
        yield put(revertObjectiveMoveActionRequest(undoActionData));
        break;
      case 6 /* Non last KR (root) of an aligned objective */:
        requestBody.reference_objective_id = nextRootObjective.objective_id;
        requestBody.objective_position = 'BEFORE';
        data = (yield objectivesApi.alignObjective(objective_id, requestBody))
          .data;
        yield put(saveRecentlyDroppedObjective(data));
        yield put(revertObjectiveMoveActionRequest(undoActionData));
        break;
      default:
        break;
    }
    yield put(hideAppLoading());
  } catch (err) {
    yield put(showAlertMessage('error', err.message));
    yield put(hideAppLoading());
    analytics.trackError('revert_combine_objective_request_worker', err);
  }
}

function* showOKRHierarchyToastWorker() {
  try {
    const selectedTab = yield select(getSelectedTeamSelector);
    if (selectedTab.tag_id === HeaderLeftTabs[2].tag_id) {
      const alertActionType = {
        actionText: 'Refresh',
        actionType: ToastActionType.OKR_HEIRARCHY_UPDATED,
        showButton: true,
        imageUrl: '/assets/images/refresh-white.svg'
      };
      // new action for this alert so that this alert doesn't go away even after we get other alerts
      yield put(
        showOKRHierarchyAlertMessage(
          AlertActionTypes.NO_ICON,
          'There is an update in the objective tree. Please refresh',
          alertActionType
        )
      );
      analytics.trackEvent(
        AnalyticsCategory.drag_and_drop,
        'Showing OKR hierarcy update toast'
      );
    }
  } catch (err) {
    yield put(showAlertMessage('error', err.message));
  }
}

function* approveAllManagerObjectivesWorker(filterData) {
  const { data: filters } = filterData;
  const selectedTeam = yield select(getSelectedTeamSelector);
  const searchParams = yield select(getSearchObjectiveParams);
  yield put(showAppLoading());
  const params = {
    user_id: filters.owners?.length ? filters.owners.join() : null,
    team_id:
      selectedTeam &&
      !isHeaderLeftTabs(selectedTeam.tag_id) &&
      !(searchParams && searchParams.searchActive)
        ? selectedTeam.tag_id
        : filters.teams?.length
        ? filters.teams.join()
        : null,
    tag_id: filters.tags?.length ? filters.tags.join() : null,
    status: filters.status?.length ? filters.status.join() : null,
    objective_type: filters.objective_type,
    okr_cycle_id: getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE),
    label_ids: filters.label_ids?.length ? filters.label_ids.join() : null,
    due_date_by: filters.due_date,
    objective_status:
      (filters.negotiation_states ? filters.negotiation_states.join() : null) ||
      (filters.objective_status ? filters.objective_status.join() : null),
    team_id_relation: filters.isTeamIntersection ? 'INTERSECTION' : 'UNION',
    tag_id_relation: filters.isTagIntersection ? 'INTERSECTION' : 'UNION',
    progress_higher_than: filters.progress_higher_than,
    progress_lower_than: filters.progress_lower_than,
    objectives_not_updated_from_days: filters.objectives_not_updated_from_days,
    search_query:
      searchParams && searchParams.searchTerm ? searchParams.searchTerm : null,
    level: filters.level,
    exclude_if_due_date_passed: !!filters.exclude_if_due_date_passed,
    manager_approval_status: filters.manager_approval_status?.length
      ? filters.manager_approval_status.join()
      : null
  };
  appendCustomRangeQueryParamsToRequest(params);
  if (filters.level) params.level_filter_type = 'SPECIFIC_LEVEL';
  try {
    if (selectedTeam.tag_id === HeaderLeftTabs[1].tag_id) {
      yield objectivesApi.bulkApproveAllObjectives(params);
      yield put(fetchFilteredObjectives(filters, false, null));
      analytics.trackEvent(
        AnalyticsCategory.api_requests,
        'approve_all_objective_with_krs'
      );
      yield put(
        showAlertMessage(
          'success',
          'All Objective and their Key Results were successfully approved'
        )
      );
    }
    yield put(hideAppLoading());
  } catch (err) {
    yield put(showAlertMessage('error', err.message));
    yield put(hideAppLoading());
    analytics.trackError('approve_all_objective_with_krs', err);
  }
}

export default function* defaultSaga() {
  yield takeLatest(
    actions.REORDER_ROOT_OBJECTIVES_REQUEST,
    reorderObjectivesWorker
  );
  yield takeLatest(
    actions.ADD_TO_OBJECTIVE_TREE_REQUEST,
    addToObjectiveTreeRequestWorker
  );
  yield takeLatest(
    actions.REORDER_UNDER_SAME_PARENT_REQUEST,
    reorderUnderSameParentRequestWorker
  );
  yield takeLatest(
    actions.REORDER_UNDER_DIFF_PARENT_REQUEST,
    reorderUnderDiffParentRequestWorker
  );
  yield takeLatest(
    actions.REVERT_OBJECTIVE_MOVE_ACTION,
    revertObjectiveMoveActionWorker
  );
  yield takeLatest(
    actions.SHOW_OKR_HIERACHY_TOAST,
    showOKRHierarchyToastWorker
  );
  yield takeLatest(
    actions.APPROVE_ALL_MANAGER_OBJECTIVES,
    approveAllManagerObjectivesWorker
  );
}
